import { Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Divider, Stack, Tooltip } from '@mui/material';

import { AccessSessionClientModel } from '@client-app-sdk';

import { useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';

import AccessSessionConnectionMethods from '@MyAccess/components/AccessSessionConnectionMethods';
import AccessSessionHeader from '@MyAccess/components/AccessSessionHeader';
import { CONNECTION_METHOD_PARAM, SESSION_ID_PARAM, SESSION_ID_REQUEST_PARAM } from '@MyAccess/utils/consts';
import { DISCONNECTED_STATUS, Status } from '@common/ui/Status';
import { MaterialIcon } from '@common/ui/MaterialIcon';
import { AnalyticsEvents } from '@common/utils/analytics/events';

interface AccessSessionProps {
  session: AccessSessionClientModel;
}

export default function AccessSession({ session }: AccessSessionProps) {
  const { isEnabled: isJitConnectEnabled } = useFlagFixed(Flags.JIT_CONNECT);

  return (
    <Fragment key={session.id}>
      <Divider />
      <Stack
        spacing={1}
        padding={1.5}
        paddingLeft={3}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <AccessSessionHeader icon={session.type.icons.svg} name={session.name} typeName={session.type.name} />
        {isJitConnectEnabled ? (
          <AccessSessionJitConnect session={session} />
        ) : (
          <AccessSessionMethodsConnect session={session} />
        )}
      </Stack>
    </Fragment>
  );
}

function AccessSessionJitConnect({ session }: { session: AccessSessionClientModel }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleConnectClick = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(SESSION_ID_REQUEST_PARAM, session.id);
    setSearchParams(newSearchParams);
  };

  const handleAccessDetailsClick = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(SESSION_ID_PARAM, session.id);
    setSearchParams(newSearchParams);
  };

  return (
    <Stack direction="row" spacing={3} alignItems="center" justifyContent="flex-end">
      <Status status={session.status} />
      {session.status === DISCONNECTED_STATUS ? (
        <Button
          onClick={handleConnectClick}
          variant="outlined"
          data-trigger={AnalyticsEvents.ConnectSessionsClicked}
          data-props={JSON.stringify({
            integrationType: session.integration.type,
            sessionType: session.type.name,
          })}
        >
          Connect
        </Button>
      ) : (
        <>
          <Button onClick={handleAccessDetailsClick} startIcon={<MaterialIcon symbol="key" />}>
            Access Details
          </Button>
          {session.has_more_access_units && (
            <Tooltip title="Add resources or permissions" placement="top" arrow>
              <Button
                onClick={handleConnectClick}
                variant="outlined-narrow"
                data-trigger={AnalyticsEvents.AddSessionClicked}
                data-props={JSON.stringify({
                  integrationType: session.integration.type,
                  sessionType: session.type.name,
                })}
              >
                <MaterialIcon symbol="add" />
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </Stack>
  );
}

function AccessSessionMethodsConnect({ session }: { session: AccessSessionClientModel }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClick = (method: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(CONNECTION_METHOD_PARAM, method);
    newSearchParams.set(SESSION_ID_PARAM, session.id);
    setSearchParams(newSearchParams);
  };

  return session.connection_methods.length > 0 ? (
    <AccessSessionConnectionMethods methods={session.connection_methods} onClick={(method) => handleClick(method)} />
  ) : null;
}
